import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import styled from 'styled-components';
import {
  UserPlus,
  Heart,
  Mail,
  Menu,
  X,
  Home
} from 'lucide-react';
import { BRAND_NAME } from '../constants/brand';

// Components
const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: ${props => props.$scrolled ? '55px' : props.theme.ui.navHeight};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${props => props.theme.spacing.lg};
  z-index: 1000;
  background-color: ${props => props.$scrolled ? 'rgba(18, 24, 40, 0.9)' : 'rgba(18, 24, 40, 0.6)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition:
    height calc(0.3s * var(--motion-duration-scale, 1)) ease,
    background-color calc(0.3s * var(--motion-duration-scale, 1)) ease,
    box-shadow calc(0.3s * var(--motion-duration-scale, 1)) ease;
  will-change: height, background-color, box-shadow;
  box-shadow: ${props => props.$scrolled ? '0 4px 20px rgba(0, 0, 0, 0.4)' : 'none'};
`;

const LogoLink = styled(Link)`
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.display};
  font-weight: 400;
  letter-spacing: 0.05em;
  font-size: 1.5rem;
  text-decoration: none;
  transition:
    color calc(0.2s * var(--motion-duration-scale, 1)) ease,
    transform calc(0.2s * var(--motion-duration-scale, 1)) ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const MobileToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: none;
  color: ${props => props.theme.colors.white};
  padding: 0.5rem;
  
  @media (max-width: 1023px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const LinksContainer = styled.div`
  height: 100%;
  
  @media (max-width: 1023px) {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background-color: ${props => props.theme.colors.bgMedium};
    flex-direction: column;
    padding: ${props => props.theme.spacing.lg};
    border-bottom: 1px solid ${props => props.theme.colors.cardBorder};
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    height: auto;
    box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    animation: fadeIn calc(0.3s * var(--motion-duration-scale, 1))
      ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const LinksList = styled.ul`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.lg};
  list-style: none;
  padding: 0;
  margin: 0;
  height: 100%;

  @media (max-width: 1023px) {
    flex-direction: column;
    align-items: flex-start;
    height: auto;
    width: 100%;
  }
`;

const StyledLink = styled(Link)`
  color: ${props => props.$active ? props.theme.colors.white : props.theme.colors.textLighter};
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  position: relative;
  transition:
    color calc(0.2s * var(--motion-duration-scale, 1)) ease,
    background-color calc(0.2s * var(--motion-duration-scale, 1)) ease;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  height: 100%;
  padding: 0.5rem ${props => props.theme.spacing.xs};
  border-radius: 6px;

  &:hover {
    color: ${props => props.theme.colors.white};
    background: rgba(255, 255, 255, 0.05);
  }

  ${props => props.$active && `
    color: ${props.theme.colors.white};
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 2px;
      background: ${props.theme.colors.primary};
      border-radius: 2px;
    }
  `}

  @media (max-width: 1023px) {
    width: 100%;
    padding: 0.75rem;
    
    ${props => props.$active && `
      background: rgba(224, 32, 32, 0.12);
      &::after { display: none; }
      border-left: 3px solid ${props.theme.colors.primary};
    `}
  }
`;

const TopNavigation = () => {
  const { t } = useTranslation();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false); // Init false to prevent hydration mismatch if SSR (though mostly CSR here)
  const [activeHash, setActiveHash] = useState('');

  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Resize Handler
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1023);
      if (window.innerWidth > 1023) setDropdownOpen(false);
    };

    // Initial check
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll Handler (Scrolled State + ScrollSpy)
  useEffect(() => {
    const handleScroll = () => {
      // 1. Navbar style
      setIsScrolled(window.scrollY > 20);

      // 2. ScrollSpy logic (only on home page)
      if (location.pathname === '/' || location.pathname === '') {
        const sections = ['home-section', 'apps-section', 'donate-section', 'contact-section'];

        // Find the section that is currently most visible
        let current = '';
        // Use a threshold relative to viewport height (e.g., 1/3 down the screen)
        // This prevents the next section from highlighting too early
        const scrollPosition = window.scrollY + (window.innerHeight * 0.3);

        // Check if we are at the bottom of the page
        if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 50) {
          current = '#contact-section'; // Or the last section in your list
        } else {
          for (const sectionId of sections) {
            const element = document.getElementById(sectionId);
            if (element) {
              const { offsetTop, offsetHeight } = element;
              if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                current = `#${sectionId}`;
              }
            }
          }
        }

        // Use hash from URL if no section is detected (e.g. top of page)
        // or prioritize detected section
        if (current) setActiveHash(current);
        else if (window.scrollY < 100) setActiveHash('#home-section');
      } else {
        setActiveHash('');
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Trigger once
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Handle active state from URL (if page loads with hash)
  useEffect(() => {
    if (location.hash) setActiveHash(location.hash);
  }, [location.hash, location.pathname]);

  // Click outside listener for mobile dropdown
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = useCallback(() => {
    setDropdownOpen(prev => !prev);
  }, []);

  const handleAnchorLink = useCallback((e, path) => {
    const [pathname, hash] = path.split('#');
    if (!hash) {
      // Plain route link (e.g. /hire): let same-route clicks rest.
      if (location.pathname === pathname) e.preventDefault();
      else navigate(path);
      setDropdownOpen(false);
      return;
    }
    if (location.pathname === pathname || pathname === '') {
      e.preventDefault();
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.pushState(null, '', `#${hash}`);
        setActiveHash(`#${hash}`);
      }
    } else {
      navigate(path);
    }
    setDropdownOpen(false);
  }, [location.pathname, navigate]);

  const mainNavItems = useMemo(() => [
    { to: '/hire', icon: UserPlus, label: t('nav.hire_me'), title: t('nav.hire_me'), isRoute: true },
    { to: '/#donate-section', icon: Heart, label: t('nav.support'), title: t('nav.support') },
    { to: '/#contact-section', icon: Mail, label: t('nav.contact'), title: t('nav.contact') }
  ], [t]);

  const mobileNavItems = useMemo(() => [
    ...mainNavItems
  ], [mainNavItems]);

  return (
    <Nav $scrolled={isScrolled} role="navigation" aria-label="Main navigation">
      <LogoLink
        to="/#home-section"
        onClick={(e) => handleAnchorLink(e, '/#home-section')}
        aria-label={`${BRAND_NAME} - Home`}
      >
        <Home size={24} strokeWidth={isMobile ? 2 : 2.5} />
        {/* Only show text if not mobile or if desired. Logo text logic: */}
        <span>{BRAND_NAME}</span>
      </LogoLink>

      {isMobile && (
        <MobileToggle
          onClick={toggleDropdown}
          aria-expanded={isDropdownOpen}
          aria-label={t('nav.menu_label')}
        >
          {isDropdownOpen ? <X size={28} /> : <Menu size={28} />}
        </MobileToggle>
      )}

      <LinksContainer $isOpen={isDropdownOpen} ref={dropdownRef}>
        <LinksList>
          {isMobile ? (
            <>
              {mobileNavItems.map(({ to, icon: Icon, label, title, isRoute }) => (
                <li key={to} style={{ width: '100%' }}>
                  <StyledLink
                    to={to}
                    title={title}
                    onClick={(e) => handleAnchorLink(e, to)}
                    $active={isRoute ? location.pathname === to : activeHash === to.substring(to.indexOf('#'))}
                    aria-current={isRoute && location.pathname === to ? 'page' : undefined}
                  >
                    <Icon size={20} />
                    <span>{label}</span>
                  </StyledLink>
                </li>
              ))}
              <li style={{ width: '100%', marginTop: '1rem' }}>
                <LanguageSwitcher />
              </li>
            </>
          ) : (
            <>
              {mainNavItems.map(({ to, icon: Icon, label, title, isRoute }) => (
                <li key={to}>
                  <StyledLink
                    to={to}
                    title={title}
                    onClick={(e) => handleAnchorLink(e, to)}
                    $active={isRoute ? location.pathname === to : activeHash === to.substring(to.indexOf('#'))}
                    aria-current={isRoute && location.pathname === to ? 'page' : undefined}
                  >
                    <Icon size={18} />
                    <span>{label}</span>
                  </StyledLink>
                </li>
              ))}
              <li>
                <LanguageSwitcher />
              </li>
            </>
          )}
        </LinksList>
      </LinksContainer>
    </Nav>
  );
};

export default TopNavigation;